import {
    IChannel,
    IPoll,
    CreateChannelPollResponse,
    ITextFieldCollection,
    IChannelComponentOrderItem,
} from "@Interfaces";
import { createSlice, CaseReducer, PayloadAction } from "@reduxjs/toolkit";

type IChannelState = {
    channels: Array<IChannel>;
    loading: boolean;
    error: boolean;
    invitation: IChannel | {};
    current: string;
    selectedAssistantContexts: Record<string, string[]>;
    assistantContextAvailabilityById: Record<
        string,
        {
            status: "idle" | "loading" | "succeeded" | "failed";
            hasAnalysisBundle: boolean;
            title?: string;
        }
    >;
};

const initialState: IChannelState = {
    channels: [],
    loading: false,
    error: false,
    invitation: {},
    current: "",
    selectedAssistantContexts: {},
    assistantContextAvailabilityById: {},
};

// ***************************************
// Queries
// ****************************************

const getChannelFromId = (channels: Array<IChannel>, channelId: string) =>
    channels.findIndex(({ _id }: { _id: string }) => _id === channelId);

const getPollFromId = (state: IChannel, pollId: string) =>
    state.polls.findIndex(({ _id }: { _id: string }) => _id === pollId);

const normalizeChannel = (channel: Partial<IChannel>): IChannel => {
    const {
        polls,
        shared_links,
        has_children,
        sub_channels,
        isHydrated,
        pollsStatus,
        detailsStatus,
        order,
        componentOrder,
        active,
        ...rest
    } = channel;

    const normalizedPolls = polls ?? [];
    const normalizedSharedLinks = shared_links ?? [];
    const normalizedHasChildren = has_children ?? false;
    const normalizedSubChannels = sub_channels ?? null;
    const normalizedPollStatus =
        pollsStatus ??
        (Array.isArray(normalizedPolls) && normalizedPolls.length > 0
            ? "succeeded"
            : "idle");
    const normalizedDetailsStatus =
        detailsStatus ??
        (typeof isHydrated === "boolean"
            ? isHydrated
                ? "succeeded"
                : "idle"
            : "idle");

    // Use the backend's order field if provided, otherwise fall back to componentOrder
    const finalComponentOrder = order ?? componentOrder;

    const normalizedActive = typeof active === "boolean" ? active : true;

    return {
        polls: normalizedPolls,
        shared_links: normalizedSharedLinks,
        has_children: normalizedHasChildren,
        sub_channels: normalizedSubChannels,
        active: normalizedActive,
        isHydrated: isHydrated ?? false,
        pollsStatus: normalizedPollStatus,
        detailsStatus: normalizedDetailsStatus,
        componentOrder: finalComponentOrder,
        ...rest,
    } as IChannel;
};

// ***************************************
// Reducers
// ****************************************

const addPollInChannel: CaseReducer<
    IChannelState,
    PayloadAction<{ poll: IPoll; channel_id: string }>
> = (state, { payload }) => {
    const { poll, channel_id } = payload;

    const index = getChannelFromId(state.channels, channel_id);

    if (index === -1) {
        return state;
    }

    if (
        state.channels[index] &&
        state.channels[index].hasOwnProperty("polls")
    ) {
        // Check if poll already exists in the array
        const pollExists = state.channels[index].polls.some(
            (p) => p._id === poll._id
        );

        if (!pollExists) {
            // Create a new array to trigger React detection of the change
            const updatedPolls = [poll, ...state.channels[index].polls];
            state.channels[index].polls = updatedPolls;
        }
    }

    return state;
};

const upsertChannelReducer: CaseReducer<
    IChannelState,
    PayloadAction<IChannel>
> = (state, { payload }) => {
    const index = getChannelFromId(state.channels, payload._id);

    if (index >= 0) {
        state.channels[index] = normalizeChannel({
            ...state.channels[index],
            ...payload,
        });
    } else {
        state.channels.push(normalizeChannel(payload));
    }

    return state;
};

const createChannel: CaseReducer<IChannelState, PayloadAction<IChannel>> = (
    state,
    action
) => upsertChannelReducer(state, action);

const createSubChannel: CaseReducer<
    IChannelState,
    PayloadAction<{ subChannel: IChannel; parentId: string }>
> = (state, { payload }) => {
    const { subChannel, parentId } = payload;

    upsertChannelReducer(state, {
        payload: subChannel,
        type: "channels/upsertSubChannel",
    });

    const parentIndex = getChannelFromId(state.channels, parentId);
    const parentChannel = state.channels[parentIndex];

    if (!parentChannel) {
        return state;
    }

    parentChannel.has_children = true;
    const summaries = parentChannel.sub_channels ?? [];
    const nextSummaries = summaries
        ? summaries.filter((item) => item._id !== subChannel._id)
        : [];

    parentChannel.sub_channels = [
        ...nextSummaries,
        {
            _id: subChannel._id,
            title: subChannel.title,
            active:
                typeof subChannel.active === "boolean"
                    ? subChannel.active
                    : true,
        },
    ];

    return state;
};

const updateChannel: CaseReducer<IChannelState, PayloadAction<IChannel>> = (
    state,
    { payload }
) => {
    upsertChannelReducer(state, {
        payload,
        type: "channels/updateChannel",
    });

    if (!payload.parent_channel_id) {
        if (payload.settings) {
            state.channels = state.channels.map((channel) =>
                String(channel.parent_channel_id) === payload._id
                    ? {
                          ...channel,
                          settings: payload.settings,
                      }
                    : channel
            );
        }

        return state;
    }

    const parentIndex = getChannelFromId(
        state.channels,
        String(payload.parent_channel_id)
    );
    const parentChannel = state.channels[parentIndex];

    if (!parentChannel || !parentChannel.sub_channels?.length) {
        return state;
    }

    parentChannel.sub_channels = parentChannel.sub_channels.map((summary) =>
        summary._id === payload._id
            ? {
                  ...summary,
                  title: payload.title ?? summary.title,
                  active:
                      typeof payload.active === "boolean"
                          ? payload.active
                          : summary.active,
                  closed:
                      typeof payload.closed === "boolean"
                          ? payload.closed
                          : summary.closed,
              }
            : summary
    );

    return state;
};

const removeChannel: CaseReducer<IChannelState, PayloadAction<IChannel>> = (
    state,
    { payload }
) => {
    state.channels = state.channels.filter(
        (item) => !(item._id === payload._id)
    );

    // If this was a sub-channel, update the parent so it reverts to a
    // normal channel when its last child is removed.
    if (payload.parent_channel_id) {
        const parentIndex = getChannelFromId(
            state.channels,
            String(payload.parent_channel_id)
        );
        const parent = state.channels[parentIndex];
        if (parent) {
            const remaining = (parent.sub_channels ?? []).filter(
                (s) => s._id !== payload._id
            );
            parent.sub_channels = remaining.length > 0 ? remaining : null;
            parent.has_children = remaining.length > 0;
        }
    }

    return state;
};

const storeChannels: CaseReducer<
    any,
    PayloadAction<Array<CreateChannelPollResponse>>
> = (state, { payload }) => {
    state.channels = payload.map((channel) => normalizeChannel(channel));
    const firstChannel = state.channels[0];
    if (firstChannel?._id) {
        state.current = firstChannel._id;
    }

    return state;
};

const storeInvitationChannel: CaseReducer<
    IChannelState,
    PayloadAction<IChannel>
> = (state, { payload }) => {
    state.invitation = payload;

    return state;
};

const removeInvitationChannel: CaseReducer<IChannelState> = (state) => {
    state.invitation = {};
    return state;
};

const storePolls: CaseReducer<
    IChannelState,
    PayloadAction<{
        polls: Array<IPoll>;
        channel_id: string;
        text_fields?: Array<ITextFieldCollection>;
    }>
> = (state, { payload }) => {
    const { polls, channel_id, text_fields } = payload;

    const index = getChannelFromId(state.channels, channel_id);

    if (index === -1) {
        return state;
    }
    state.channels[index].text_fields = text_fields ?? [];
    state.channels[index].polls = polls;
    state.channels[index].pollsStatus = "succeeded";

    return state;
};

const setChannelComponentOrder: CaseReducer<
    IChannelState,
    PayloadAction<{
        channelId: string;
        order: IChannelComponentOrderItem[];
    }>
> = (state, { payload }) => {
    const { channelId, order } = payload;
    const index = getChannelFromId(state.channels, channelId);
    if (index === -1) {
        return state;
    }

    state.channels[index].componentOrder = order;
    return state;
};

const setChannelPollsStatus: CaseReducer<
    IChannelState,
    PayloadAction<{
        channelId: string;
        status: NonNullable<IChannel["pollsStatus"]>;
    }>
> = (state, { payload }) => {
    const { channelId, status } = payload;
    const index = getChannelFromId(state.channels, channelId);
    if (index === -1) {
        return state;
    }

    state.channels[index].pollsStatus = status;

    return state;
};

const setChannelDetailsStatus: CaseReducer<
    IChannelState,
    PayloadAction<{
        channelId: string;
        status: NonNullable<IChannel["detailsStatus"]>;
    }>
> = (state, { payload }) => {
    const { channelId, status } = payload;
    const index = getChannelFromId(state.channels, channelId);
    if (index === -1) {
        return state;
    }

    state.channels[index].detailsStatus = status;
    state.channels[index].isHydrated = status === "succeeded";

    return state;
};

const setActiveChannelId: CaseReducer<IChannelState, PayloadAction<string>> = (
    state,
    { payload }
) => {
    state.current = payload;
    return state;
};

const setSelectedAssistantContexts: CaseReducer<
    IChannelState,
    PayloadAction<{
        channelId: string;
        contextIds: string[];
    }>
> = (state, { payload }) => {
    const { channelId, contextIds } = payload;

    if (!channelId) {
        return state;
    }

    state.selectedAssistantContexts[channelId] = contextIds;

    return state;
};

const setAssistantContextAvailability: CaseReducer<
    IChannelState,
    PayloadAction<{
        channelId: string;
        status: "idle" | "loading" | "succeeded" | "failed";
        hasAnalysisBundle: boolean;
        title?: string;
    }>
> = (state, { payload }) => {
    const { channelId, status, hasAnalysisBundle, title } = payload;

    if (!channelId) {
        return state;
    }

    state.assistantContextAvailabilityById[channelId] = {
        status,
        hasAnalysisBundle,
        title,
    };

    return state;
};

const updatePollInChannel: CaseReducer<
    IChannelState,
    PayloadAction<{ poll: IPoll }>
> = (state, { payload }) => {
    const { poll } = payload;

    // Some API responses (e.g., NPS votes) omit channel_id; fall back to the active channel since state.current is always set when a poll is rendered.
    const channelId = poll.channel_id || state.current;
    if (!channelId) return state;

    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    const pollIndex = getPollFromId(state.channels[channelIndex], poll._id);
    if (pollIndex === -1) return state;

    // Create a new array to trigger React detection of the change
    const updatedPolls = [...state.channels[channelIndex].polls];
    updatedPolls[pollIndex] = poll;
    state.channels[channelIndex].polls = updatedPolls;

    return state;
};

const storeSharedLinkInChannel: CaseReducer<
    IChannelState,
    PayloadAction<{ link_id: string; channel_id: string }>
> = (state, { payload }) => {
    const { link_id, channel_id } = payload;

    const index = getChannelFromId(state.channels, channel_id);

    if (index === -1) {
        return state;
    }

    if (
        state.channels[index] &&
        !state.channels[index].hasOwnProperty("shared_links")
    ) {
        state.channels[index] = {
            ...state.channels[index],
            shared_links: [],
        };
    }

    state.channels[index].shared_links.push(link_id);

    return state;
};

const isChannelActionLoading: CaseReducer<IChannelState> = (state) => {
    state.loading = true;

    return state;
};

const isChannelActionLoaded: CaseReducer<IChannelState> = (state) => {
    state.loading = false;

    return state;
};

const hasErrors: CaseReducer<IChannelState> = (state) => {
    state.error = true;

    return state;
};

const closePoll: CaseReducer<
    IChannelState,
    PayloadAction<{ pollId: string; channelId: string }>
> = (state, { payload }) => {
    const { pollId, channelId } = payload;
    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    const pollIndex = getPollFromId(state.channels[channelIndex], pollId);
    if (pollIndex === -1) return state;

    state.channels[channelIndex].polls[pollIndex].active = false;

    return state;
};

const removePoll: CaseReducer<
    IChannelState,
    PayloadAction<{ pollId: string; channelId: string }>
> = (state, { payload }) => {
    const { pollId, channelId } = payload;
    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    state.channels[channelIndex].polls = state.channels[
        channelIndex
    ].polls.filter((item) => !(item._id === pollId));

    return state;
};

//@4  ***************************************
//@4               COMMENTS
//@4  ****************************************

const updateCommentsInPoll: CaseReducer<
    IChannelState,
    PayloadAction<{
        pollId: string;
        channelId: string;
        commentId: string;
        updatedComment: string;
    }>
> = (state, { payload }) => {
    const { pollId, channelId, commentId, updatedComment } = payload;

    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    const pollIndex = getPollFromId(state.channels[channelIndex], pollId);
    if (pollIndex === -1) return state;

    state.channels[channelIndex].polls[pollIndex].comments.list.filter(
        (comment) => comment._id === commentId
    )[0].body = updatedComment;
    return state;
};

type CommentToggleElement = keyof Pick<
    IPoll["comments"],
    "isExpanded" | "isLoading" | "loadMore"
>;

const toggleCommentsElementState: CaseReducer<
    IChannelState,
    PayloadAction<{
        pollId: string;
        channelId: string;
        element: CommentToggleElement;
    }>
> = (state, { payload }) => {
    const { pollId, channelId, element } = payload;

    if (state.channels.length === 0) return state;

    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    const pollIndex = getPollFromId(state.channels[channelIndex], pollId);
    if (pollIndex === -1) return state;

    const currentValue =
        state.channels[channelIndex].polls[pollIndex].comments[element];
    state.channels[channelIndex].polls[pollIndex].comments[element] =
        !currentValue;

    return state;
};

const addCommentInPollByChannel: CaseReducer<
    IChannelState,
    PayloadAction<{
        pollId: string;
        channelId: string;
        commentData: any;
    }>
> = (state, { payload }) => {
    const { channelId, pollId, commentData } = payload;

    const channelIndex = getChannelFromId(state.channels, channelId);
    const pollIndex = getPollFromId(state.channels[channelIndex], pollId);
    state.channels[channelIndex].polls[pollIndex].comments.count += 1;
    state.channels[channelIndex].polls[pollIndex].comments.list.unshift(
        commentData
    );
    return state;
};

const storePollComments: CaseReducer<
    IChannelState,
    PayloadAction<{
        pollId: string;
        channelId: string;
        commentsList: any[];
    }>
> = (state, { payload }) => {
    const { pollId, channelId, commentsList } = payload;

    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    const pollIndex = getPollFromId(state.channels[channelIndex], pollId);
    if (pollIndex === -1) return state;

    state.channels[channelIndex].polls[pollIndex].comments.list.push(
        ...commentsList
    );
    state.channels[channelIndex].polls[pollIndex].comments.isLoading = false;
    return state;
};

const removeComment: CaseReducer<
    IChannelState,
    PayloadAction<{ channelId: string; pollId: string; commentId: string }>
> = (state, { payload }) => {
    const { channelId, pollId, commentId } = payload;

    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    const pollIndex = getPollFromId(state.channels[channelIndex], pollId);
    if (pollIndex === -1) return state;

    state.channels[channelIndex].polls[pollIndex].comments.list =
        state.channels[channelIndex].polls[pollIndex].comments.list.filter(
            (comment) => comment._id !== commentId
        );

    state.channels[channelIndex].polls[pollIndex].comments.count -= 1;

    return state;
};

const toggleLikePollInPoll: CaseReducer<
    IChannelState,
    PayloadAction<{ pollId: string; channelId: string; updatedLikes: string[] }>
> = (state, { payload }) => {
    const { pollId, channelId, updatedLikes } = payload;
    const channelIndex = getChannelFromId(state.channels, channelId);
    if (channelIndex === -1) return state;

    const pollIndex = getPollFromId(state.channels[channelIndex], pollId);
    if (pollIndex === -1) return state;

    state.channels[channelIndex].polls[pollIndex].likes = [...updatedLikes];

    return state;
};

const channelSlice = createSlice({
    name: "channel",
    initialState,
    reducers: {
        addPollInChannel,
        createChannel,
        createSubChannel,
        updateChannel,
        upsertChannel: upsertChannelReducer,
        removeChannel,
        closePoll,
        storeChannels,
        storeInvitationChannel,
        removeInvitationChannel,
        storePolls,
        setChannelPollsStatus,
        setChannelDetailsStatus,
        setActiveChannelId,
        setSelectedAssistantContexts,
        setAssistantContextAvailability,
        updatePollInChannel,
        storeSharedLinkInChannel,
        isChannelActionLoading,
        isChannelActionLoaded,
        hasErrors,
        removePoll,
        toggleCommentsElementState,
        addCommentInPollByChannel,
        storePollComments,
        removeComment,
        updateCommentsInPoll,
        toggleLikePollInPoll,
        setChannelComponentOrder,
    },
});

export const { name, actions } = channelSlice;

export default channelSlice.reducer;
